/** Groceries group by the order you walk a shop, not alphabetically, so the
 *  list matches your route. The order itself is unchanged since WP 4.4 —
 *  editable per list, saved in `localStorage` keyed by these exact names
 *  (`GroceryList.tsx`'s own comment), which is why a name here must never be
 *  renamed. Adding one is safe: `loadOrder` appends an aisle a saved order
 *  does not know to the end. `Health & beauty` arrived that way on 8 Oct
 *  2026 (docs/recipe-import-plan): Bengay, toothpaste and body wash had
 *  been sharing Household with bin bags. */
export const AISLES = [
  'Produce', 'Bakery', 'Dairy & eggs', 'Meat & fish', 'Pantry', 'Frozen', 'Drinks', 'Household',
  'Health & beauty', 'Other',
]

// `.ts` spelled out on both imports below: `node --test` resolves every hop
// of an import chain it reaches directly rather than through Vite, and its
// ESM resolver will not guess an extension — see tsconfig.json's own
// comment on `allowImportingTsExtensions`.
import { normalise, matchAisle, aisleFor, mergeKnown, toAisleMap, aisleWriteTargets, type AisleMap, type Dict } from './aisleMatch.ts'

// Re-exported rather than re-implemented: `groceryAisles.ts`'s
// `useSetAisleOverride` needs the same normalised key this file computes a
// term's aisle from, so "Move to…" writes under the identical key `aisleOf`
// would read back — one function, not two that could drift.
export { normalise, aisleFor, mergeKnown, toAisleMap, aisleWriteTargets, type AisleMap, type Dict }

const EMPTY: Dict = []
let loaded: Dict | null = null
let loading: Promise<Dict> | null = null

/**
 * The dictionary (docs/recipe-import-plan, package A): 22,000-odd terms
 * built by `scripts/dict/build.mjs` from Instacart's product list, the Open
 * Food Facts taxonomies, a hand list and the 2026-09 seed it replaced.
 * 112 KB gzipped, so it is its own chunk, fetched the first time a grocery
 * list mounts (`useAisleDict` in `groceryAisles.ts`) and held here for the
 * rest of the session. Nothing at runtime ever writes to it — what people
 * correct and what the Worker learns live in `grocery_aisle_overrides` and
 * `grocery_aisles`, passed in as `known`.
 */
export function loadAisleDict(): Promise<Dict> {
  loading ??= import('./aisleDict.ts').then(m => {
    loaded = Object.entries(m.AISLE_DICT_TEXT).flatMap(([aisle, text]) =>
      text.split('\n').filter(Boolean).map(term => [term, aisle] as const))
    return loaded
  })
  return loading
}

/** The dictionary if the chunk has arrived, else empty: a render that runs
 *  before it files everything under Other for a frame and never throws. */
export function aisleDictNow(): Dict {
  return loaded ?? EMPTY
}

/**
 * Search order: the person's own corrections, then the shared vocabulary —
 * what the Worker has learned and what an admin corrected (`known`, merged
 * by `mergeKnown` from `grocery_aisle_overrides` and `grocery_aisles` by the
 * caller's own hook — this file stays synchronous and pure given its
 * inputs), then the dictionary, then `'Other'`. `known` defaults to `[]` so
 * every caller and test that passes nothing still compiles; `dict` defaults
 * to whatever has loaded, so a caller that cares whether it has (the
 * grocery list, which must not ask the Worker about a term the dictionary
 * would have filed) passes it explicitly.
 */
export function aisleOf(text: string, known: Dict = EMPTY, dict: Dict = aisleDictNow()): string {
  const hit = matchAisle(text, known) ?? matchAisle(text, dict)
  return hit?.aisle ?? 'Other'
}

/**
 * Whether a grocery add should ask the Worker's `/aisle` route about this
 * term: only when neither the learned/override dictionary nor the built one
 * found anything at all. Kept as its own pure function so the two decisions
 * — "what aisle" and "should we ask" — stay two separate facts instead of
 * one value doing both jobs (CLAUDE.md's own recurring bug).
 */
export function needsLookup(text: string, known: Dict, dict: Dict = aisleDictNow()): boolean {
  return matchAisle(text, known) === null && matchAisle(text, dict) === null
}
